import { autocapturePlugin } from '../plugins/autocapture';
import { spaRouterPlugin } from '../plugins/spa-router';
import type { VeoClient, VeoConfig } from '../types';
import { resolveApiUrl } from './api-url';
import { maybeLoadBuilder } from './builder-loader';
import { Client } from './client';

/**
 * Setup común del cliente, compartido por el entry ESM/CJS (`index.ts`, guías
 * estáticas) y el IIFE (`veo.ts`, guías perezosas). NO importa el plugin de
 * guías: la instalación va por el callback `onGuides`, así el bundle IIFE puede
 * quedar sin los renderers.
 *
 * Hace: client + spa-router + autocapture, instala guías vía callback, asigna
 * `window.veo` drenando la cola del snippet, y dispara el loader del builder.
 */
export function setupClient(
  config: VeoConfig,
  onGuides: (client: VeoClient) => void,
  loadBuilder?: () => Promise<unknown>,
): VeoClient {
  const client = new Client(config);

  client.use(spaRouterPlugin());

  if (config.autocapture !== false) {
    const autocaptureConfig = typeof config.autocapture === 'object' ? config.autocapture : {};
    client.use(autocapturePlugin(autocaptureConfig));
  }

  // Guías (opt-in): el caller decide instalarlas sync (bundler) o lazy (IIFE).
  onGuides(client);

  if (typeof window !== 'undefined') {
    const stub = (window as unknown as { veo?: { _q?: unknown[][] } }).veo;
    const pending = stub?._q ?? [];

    (window as unknown as { veo: VeoClient }).veo = client;

    for (const entry of pending) {
      const [method, args] = entry;
      if (method === 'init') continue; // ya inicializado
      const fn = (client as unknown as Record<string, (...a: unknown[]) => unknown>)[
        method as string
      ];
      if (typeof fn === 'function') {
        try {
          fn.apply(client, Array.from(args as ArrayLike<unknown>));
        } catch (err) {
          if (config.debug) console.error('[veo] drain error:', err);
        }
      }
    }

    maybeLoadBuilder({
      ...(config.builderUrl !== undefined ? { builderUrl: config.builderUrl } : {}),
      ...(config.debug !== undefined ? { debug: config.debug } : {}),
      ...(config.apiKey !== undefined ? { apiKey: config.apiKey } : {}),
      apiUrl: resolveApiUrl(config),
      ...(loadBuilder ? { loadBuilder } : {}),
    });
  }

  return client;
}
